/**
 * Visitor acquisition-source tracking.
 *
 * Detects where a visitor came from (WhatsApp group, LinkedIn, Google, a
 * friend's link, or straight to the site) and remembers it for the whole
 * browsing session so the source is still known when they sign up, register
 * for a free live session, or buy a plan minutes later.
 */

export const REFERRAL_SOURCES = [
  "whatsapp",
  "linkedin",
  "instagram",
  "telegram",
  "youtube",
  "facebook",
  "google",
  "friend",
  "direct",
  "other",
] as const;

export type ReferralSource = (typeof REFERRAL_SOURCES)[number];

export const REFERRAL_LABELS: Record<ReferralSource, string> = {
  whatsapp: "WhatsApp Group / Community",
  linkedin: "LinkedIn",
  instagram: "Instagram",
  telegram: "Telegram",
  youtube: "YouTube",
  facebook: "Facebook",
  google: "Google Search",
  friend: "Friend / Senior / Colleague",
  direct: "Direct / Website",
  other: "Other",
};

/** Short badge label used in admin tables. */
export const REFERRAL_BADGE: Record<ReferralSource, { label: string; className: string }> = {
  whatsapp: { label: "WhatsApp", className: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  linkedin: { label: "LinkedIn", className: "bg-sky-100 text-sky-800 border-sky-200" },
  instagram: { label: "Instagram", className: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200" },
  telegram: { label: "Telegram", className: "bg-cyan-100 text-cyan-800 border-cyan-200" },
  youtube: { label: "YouTube", className: "bg-red-100 text-red-800 border-red-200" },
  facebook: { label: "Facebook", className: "bg-blue-100 text-blue-800 border-blue-200" },
  google: { label: "Google", className: "bg-amber-100 text-amber-800 border-amber-200" },
  friend: { label: "Referral", className: "bg-violet-100 text-violet-800 border-violet-200" },
  direct: { label: "Direct", className: "bg-muted text-muted-foreground border-border" },
  other: { label: "Other", className: "bg-muted text-muted-foreground border-border" },
};

const STORAGE_KEY = "welivdit:acq-source";
const LANDING_KEY = "welivdit:acq-landing";

/** Map a free-text tag or hostname onto one of our known channels. */
export function normalizeSource(raw: string | null | undefined): ReferralSource | null {
  const v = (raw || "").trim().toLowerCase();
  if (!v) return null;
  if (/whatsapp|wa\.me|chat\.whatsapp/.test(v)) return "whatsapp";
  if (/linkedin|lnkd\.in/.test(v)) return "linkedin";
  if (/instagram|ig\b|insta/.test(v)) return "instagram";
  if (/telegram|t\.me/.test(v)) return "telegram";
  if (/youtube|youtu\.be/.test(v)) return "youtube";
  if (/facebook|fb\.com|fb\b/.test(v)) return "facebook";
  if (/google|bing|duckduckgo|yahoo|search/.test(v)) return "google";
  if (/friend|referral|senior|word|wom/.test(v)) return "friend";
  if (/direct|website|none/.test(v)) return "direct";
  if ((REFERRAL_SOURCES as readonly string[]).includes(v)) return v as ReferralSource;
  return null;
}

function detect(): ReferralSource {
  try {
    const params = new URLSearchParams(window.location.search);
    const tagged =
      params.get("ref") ||
      params.get("utm_source") ||
      params.get("source") ||
      params.get("via");
    const fromTag = normalizeSource(tagged);
    if (fromTag) return fromTag;

    const referrer = document.referrer || "";
    if (referrer) {
      const host = new URL(referrer).hostname;
      // Same-site navigation is not an acquisition source.
      if (host && host !== window.location.hostname) {
        const fromRef = normalizeSource(host);
        if (fromRef) return fromRef;
        return "other";
      }
    }
  } catch {
    /* detection is best-effort */
  }
  return "direct";
}

/**
 * Returns the visitor's acquisition source, detecting and storing it the very
 * first time it is called in a browsing session.
 */
export function getReferralSource(): ReferralSource {
  if (typeof window === "undefined") return "direct";
  try {
    const stored = normalizeSource(window.sessionStorage.getItem(STORAGE_KEY));
    if (stored) return stored;
  } catch {
    return detect();
  }
  const found = detect();
  try {
    window.sessionStorage.setItem(STORAGE_KEY, found);
    if (!window.sessionStorage.getItem(LANDING_KEY)) {
      window.sessionStorage.setItem(LANDING_KEY, window.location.pathname);
    }
  } catch {
    /* private mode */
  }
  return found;
}

/** The first page of the visit (useful for landing-page reporting). */
export function getLandingPage(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(LANDING_KEY);
  } catch {
    return null;
  }
}

/** Human label for a stored source value. */
export function referralLabel(raw: string | null | undefined): string {
  const s = normalizeSource(raw);
  return s ? REFERRAL_LABELS[s] : "Unknown";
}

/** Badge styling for a stored source value. */
export function referralBadge(raw: string | null | undefined) {
  const s = normalizeSource(raw);
  return s ? REFERRAL_BADGE[s] : { label: "Unknown", className: "bg-muted text-muted-foreground border-border" };
}
